import React from 'react';

import { CurrencyFormatterContextProvider } from '~/components/CurrencyFormatterContext';
import { AmountCurrencyFormattedCell } from '~/components/Finances/FinancesTable/AmountCurrencyFormattedCell/AmountCurrencyFormattedCell';

export interface VatDetailsCellInput {
  isVatIncluded: boolean | null | undefined;
  vatAmount: number | null | undefined;
  // Currency that both `vatAmount` and `amount` are expressed in. Callers must pass amount and currency
  // from the same basis (e.g. both original-request currency, or both policy currency) so Net stays correct.
  currency: string | null | undefined;
  amount?: number | null | undefined;
  // Set this when the caller's own Amount column already shows the net amount: the cell then renders
  // "Gross + VAT" instead of "VAT + Net", keeping the gross figure discoverable. Defaults to VAT + Net.
  showGrossInsteadOfNet?: boolean;
}

// vat_amount is captured at request time and is not re-derived when the payment is approved at an
// adjusted-down decision_amount, so amount - vatAmount can come out negative. Clamp at 0 the way the
// backend's net sums do (PayableWithReserve.net_paid_sum / net_pending_sum).
// Returns null when either figure is not a real number, so no caller can render NaN/Infinity.
export const getNetAmount = (amount: number | null | undefined, vatAmount: number | null | undefined): number | null =>
  typeof amount === 'number' && Number.isFinite(amount) && typeof vatAmount === 'number' && Number.isFinite(vatAmount)
    ? Math.max(0, amount - vatAmount)
    : null;

export const renderVatDetailsCell = ({
  isVatIncluded,
  vatAmount,
  currency,
  amount,
  showGrossInsteadOfNet,
}: VatDetailsCellInput): React.ReactNode => {
  if (isVatIncluded === null || isVatIncluded === undefined) return '-';
  if (!isVatIncluded) return 'Not included';
  if (vatAmount === null || vatAmount === undefined) return '-';

  // Only show an amount-derived line when the amount is a real number, so the cell never renders NaN/Infinity.
  const grossAmount = typeof amount === 'number' && Number.isFinite(amount) ? amount : null;
  const netAmount = getNetAmount(grossAmount, vatAmount);

  const vatLine = (
    <div>
      <AmountCurrencyFormattedCell amount={vatAmount} />
    </div>
  );

  return (
    <CurrencyFormatterContextProvider currency={currency || 'USD'}>
      {showGrossInsteadOfNet ? (
        <>
          {grossAmount !== null && (
            <div>
              Gross: <AmountCurrencyFormattedCell amount={grossAmount} />
            </div>
          )}
          {vatLine}
        </>
      ) : (
        <>
          {vatLine}
          {netAmount !== null && (
            <div>
              Net: <AmountCurrencyFormattedCell amount={netAmount} />
            </div>
          )}
        </>
      )}
    </CurrencyFormatterContextProvider>
  );
};
